import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Airplane, Upload, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTrackPageView } from "@/lib/useTrackPageView";

const BG = "https://images.unsplash.com/photo-1536152470836-b943b246224c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxiYWxpJTIwbGFuZHNjYXBlJTIwbmlnaHR8ZW58MHx8fHwxNzgxMjEyNjY1fDA&ixlib=rb-4.1.0&q=85";

export default function PublicRegistration() {
  useTrackPageView("main");
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    telegram_username: "",
    broker_account_number: "",
    broker_server: "",
    account_type: "Standard",
    deposit_amount: "",
    agreed_terms: false,
    acknowledged_risk: false,
  });
  const [proofPath, setProofPath] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/files/upload-public", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProofPath(data.storage_path);
      toast.success("Proof uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed_terms || !form.acknowledged_risk) {
      toast.error("You must agree to terms and risk disclaimer");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        deposit_amount: parseFloat(form.deposit_amount) || 0,
        proof_file_url: proofPath,
      };
      const { data } = await api.post("/participants/register", payload);
      setSuccess(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="fixed inset-0 bg-[#0B0F19]/85 backdrop-blur-sm" />
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-2xl">
          <Link to="/login" className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-white mb-4">
            <ArrowLeft size={14} /> Back to login
          </Link>

          {success ? (
            <div data-testid="register-success" className="bg-[#111827]/95 border border-emerald-500/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={36} weight="duotone" className="text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white mb-2">Registration submitted</h2>
              <p className="text-sm text-gray-400 max-w-md mx-auto">
                Your registration has been submitted. Our admin team will verify your trading account.
                Your lot volume will only be counted after your registration is approved.
              </p>
              {success.duplicate_flag && (
                <p className="mt-4 text-xs px-3 py-2 rounded-md bg-orange-500/10 border border-orange-500/30 text-orange-300 inline-block">
                  ⚠ A registration with this account number already exists. Admin will review.
                </p>
              )}
              <Link to="/public-leaderboard" className="mt-6 inline-block text-sm text-blue-400 hover:text-blue-300">
                View public leaderboard →
              </Link>
            </div>
          ) : (
            <div className="bg-[#111827]/95 border border-white/10 rounded-xl shadow-2xl backdrop-blur-xl">
              <div className="px-8 pt-8 pb-4 border-b border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <Airplane size={28} weight="duotone" className="text-orange-400" />
                  <span className="text-xs uppercase tracking-[0.3em] text-orange-400 font-mono">Public Registration</span>
                </div>
                <h1 className="font-display text-3xl font-bold text-white">Road To Bali Challenge</h1>
                <p className="text-sm text-gray-400 mt-1">Trade 30 valid lots in 3 months. Win a trip to Bali.</p>
              </div>

              <form onSubmit={submit} className="p-8 space-y-4" data-testid="register-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full name" required>
                    <input data-testid="reg-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="input" />
                  </Field>
                  <Field label="Email" required>
                    <input data-testid="reg-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="input" />
                  </Field>
                  <Field label="Phone" required>
                    <input data-testid="reg-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input" />
                  </Field>
                  <Field label="Telegram username">
                    <input data-testid="reg-telegram" value={form.telegram_username} onChange={(e) => update("telegram_username", e.target.value)} className="input" placeholder="@yourhandle" />
                  </Field>
                  <Field label="Broker account number" required>
                    <input data-testid="reg-account" required value={form.broker_account_number} onChange={(e) => update("broker_account_number", e.target.value)} className="input" />
                  </Field>
                  <Field label="Broker server" required>
                    <input data-testid="reg-server" required value={form.broker_server} onChange={(e) => update("broker_server", e.target.value)} className="input" placeholder="e.g. MBE-Live01" />
                  </Field>
                  <Field label="Account type" required>
                    <select data-testid="reg-type" required value={form.account_type} onChange={(e) => update("account_type", e.target.value)} className="input">
                      <option>Standard</option><option>ECN</option><option>Pro</option><option>VIP</option>
                    </select>
                  </Field>
                  <Field label="Deposit amount (USD)" required>
                    <input data-testid="reg-deposit" required type="number" min="0" step="0.01" value={form.deposit_amount} onChange={(e) => update("deposit_amount", e.target.value)} className="input" />
                  </Field>
                </div>

                <Field label="Upload proof of account / deposit (image or PDF)">
                  <label className="flex items-center gap-3 px-4 py-3 rounded-md border border-dashed border-white/20 bg-[#0B0F19] cursor-pointer hover:border-blue-500/50 transition-colors">
                    <Upload size={18} className="text-gray-400" />
                    <span className="text-sm text-gray-300">{proofPath ? "Uploaded ✓" : "Click to upload"}</span>
                    <input data-testid="reg-proof" type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" />
                  </label>
                </Field>

                <div className="space-y-2 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input data-testid="reg-terms" type="checkbox" checked={form.agreed_terms} onChange={(e) => update("agreed_terms", e.target.checked)} className="mt-1" />
                    <span className="text-sm text-gray-300">
                      I have read and agree to the{" "}
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="reg-terms-link"
                        className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
                      >
                        Terms &amp; Conditions
                      </Link>
                      , including milestone, payout and Bali qualification rules.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input data-testid="reg-risk" type="checkbox" checked={form.acknowledged_risk} onChange={(e) => update("acknowledged_risk", e.target.checked)} className="mt-1" />
                    <span className="text-sm text-gray-300">I acknowledge the trading risk disclaimer. Forex/CFD trading carries significant risk of loss.</span>
                  </label>
                  <p className="text-[11px] text-gray-500 pl-6 pt-1">
                    A timestamped record of your acceptance will be stored for audit purposes.
                  </p>
                </div>

                <button
                  type="submit"
                  data-testid="reg-submit"
                  disabled={submitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-3 rounded-md transition-colors shadow-[0_4px_30px_rgba(249,115,22,0.35)]"
                >
                  {submitting ? "Submitting..." : "Submit Registration"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
      <style>{`.input{width:100%;background:#0B0F19;border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:.6rem .9rem;color:#fff;font-size:.875rem;outline:none;transition:.15s}.input:focus{border-color:#2563EB;box-shadow:0 0 0 1px #2563EB}`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
        {label} {required && <span className="text-orange-400">*</span>}
      </span>
      {children}
    </label>
  );
}
